import React, { useState } from "react";
import TTSModal from "./TTSModal";
import { Model } from "../types";

interface ModelSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedModelIds: string[]) => void;
  models: Model[];
  maxSelections: number;
  filter?: (model: Model) => boolean;
  title: string;
  description: string;
}

export const ModelSelectionModal = ({
  isOpen,
  onClose,
  onConfirm,
  models,
  maxSelections,
  filter,
  title,
  description
}: ModelSelectionModalProps) => {
  const [selectedModels, setSelectedModels] = useState<string[]>([]);

  const eligibleModels = filter ? models.filter(filter) : models;

  const handleModelToggle = (modelId: string) => {
    setSelectedModels(prev => {
      if (prev.includes(modelId)) {
        return prev.filter(id => id !== modelId);
      } else if (prev.length < maxSelections) {
        return [...prev, modelId];
      }
      return prev;
    });
  };

  const handleConfirm = () => {
    onConfirm(selectedModels);
    setSelectedModels([]);
  };

  const handleClose = () => {
    setSelectedModels([]);
    onClose();
  };

  const [ttsModel, setTtsModel] = useState<Model | null>(null);
  const [showTts, setShowTts] = useState(false);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3">
      <div className="ledger-shell max-w-2xl w-full max-h-[80vh] overflow-y-auto text-sm">
        <div className="p-4">
          <h2 className="text-base font-semibold uppercase tracking-wider">{title}</h2>
          <p className="text-xs opacity-90 mt-2">{description}</p>
          <p className="text-xs opacity-70 mt-1">
            Select up to {maxSelections} model{maxSelections !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
          {eligibleModels.length === 0 ? (
            <div className="text-center py-8 opacity-70">
              No eligible models found
            </div>
          ) : (
            eligibleModels.map(model => (
              <div
                key={model.id}
                onClick={() => handleModelToggle(model.id)}
                className={`p-3 cursor-pointer transition-colors ledger-panel ${
                  selectedModels.includes(model.id)
                    ? 'opacity-100'
                    : 'hover:opacity-95'
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedModels.includes(model.id)}
                    readOnly
                    className="w-4 h-4"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-[#111]">{model.name}</div>
                    <div className="text-xs text-[#777]">
                      {model.class} • {model.type?.toUpperCase()} • XP: {model.xp}
                      {model.alive ? '' : ' • DECEASED'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-xs font-semibold text-[#777]">
                      {model.alive ? 'ALIVE' : 'DEAD'}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setTtsModel(model); setShowTts(true); }}
                      className="ledger-btn px-2 py-1 text-xs"
                      title="Open TTS"
                    >
                      TTS
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="p-4 flex gap-2">
          <button
            onClick={handleConfirm}
            disabled={selectedModels.length === 0}
            className="flex-1 ledger-btn disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 text-xs font-semibold uppercase tracking-wider"
          >
            Confirm Selection ({selectedModels.length}/{maxSelections})
          </button>
          <button
            onClick={handleClose}
            className="flex-1 ledger-btn px-3 py-2 text-xs font-semibold uppercase tracking-wider"
          >
            Cancel
          </button>
        </div>
      </div>
      <TTSModal isOpen={showTts} model={ttsModel} onClose={() => { setShowTts(false); setTtsModel(null); }} />
    </div>
  );
};
